import { useState } from 'react'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { PiPlus, PiCalendarBlank } from 'react-icons/pi'

import { DataTableProvider } from 'components/DataTable'
import FbButton from 'components/FbUI/FbButton'
import { FBPage } from 'components/FbUI/Page/FBPage'
import {
  FBPageHeader,
  FBPageTableContainer,
  FBPageTitle,
  FBPageTitleRow,
  FBPageInnerTabsContainer,
} from 'components/FbUI/Page/styles'
import { Tabs } from 'components/FbUI/Tabs'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { TabPanel } from 'components/FbUI/PillTabs'
import { cn } from 'components/UI/cn'
import {
  segmentedPillGroupClass,
  segmentedPillBaseClass,
  segmentedPillActiveClass,
  segmentedPillInactiveClass,
} from 'components/FbUI/segmentedPillButton'
import {
  DateRangePicker,
  type DateRangeValue,
} from 'pages/app/deals/DateRangePicker'
import { ALL_CAMPAIGNS_TABLE_KEY } from 'constants/tableQueryKeys'
import { useGetCampaignStats } from 'services/campaigns'
import { CampaignsStatsBar } from 'features/campaigns/CampaignsStatsBar'
import { CampaignsTable } from 'features/campaigns/CampaignsTable'
import { CampaignRepliesTab } from 'features/campaigns/CampaignRepliesTab'
import { CreateCampaignModalV2 } from 'features/campaigns/CreateCampaignModalV2'
import { CreateEditCampaignModal } from 'features/campaigns/CreateEditCampaignModal'
import { useGetReplyThreadSummary } from 'features/campaigns/replies_api'
import { Campaign } from 'models/campaign'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { StageColorProvider } from 'components/SalesStage/StageColorContext'

type PageTab = 'campaigns' | 'replies'

const STATS_PERIOD_OPTIONS = [
  { key: 'all', label: 'All Time' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
] as const
type StatsPeriod = (typeof STATS_PERIOD_OPTIONS)[number]['key'] | 'custom'

const STATS_PILL_BASE = cn(segmentedPillBaseClass, 'gap-1.5')

// The campaigns tab reserves room for the stats bar + period selector that the
// replies tab hides, so the replies panel is taller by that ~130px delta.
const STATS_SECTION_OFFSET_PX = 130
const CAMPAIGNS_PANEL_HEIGHT = `calc(var(--base-page-table-height) - 272px)`
const REPLIES_PANEL_HEIGHT = `calc(var(--base-page-table-height) - ${272 - STATS_SECTION_OFFSET_PX}px)`

export function CampaignsPage() {
  const featureFlag = useFeatureFlagService()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<PageTab>('campaigns')
  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>('all')
  const [statsDateRange, setStatsDateRange] =
    useState<DateRangeValue>(undefined)
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showV2Modal, setShowV2Modal] = useState<boolean>(false)
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>()

  function handleRowEdit(campaign: Campaign) {
    setShowModal(true)
    setSelectedCampaign(campaign)
  }

  function openModal() {
    if (featureFlag.enableEmailSequencing) {
      setShowV2Modal(true)
    } else {
      setShowModal(true)
    }
  }

  function hideModal() {
    setShowModal(false)
    setSelectedCampaign(undefined)
  }

  const { data: stats } = useGetCampaignStats(
    featureFlag.enableEmailSequencing,
    statsPeriod,
    statsDateRange?.from ? statsDateRange : undefined
  )
  const { data: replySummary } = useGetReplyThreadSummary()

  const customRangeLabel = statsDateRange?.from
    ? statsDateRange.to
      ? `${format(statsDateRange.from, 'd MMM')} – ${format(statsDateRange.to, 'd MMM')}`
      : format(statsDateRange.from, 'd MMM')
    : null

  if (!featureFlag.isLoading && !featureFlag.enableCampaigns) {
    navigate('/')
    return null
  }

  if (featureFlag.enableEmailSequencing) {
    return (
      <DataTableProvider tableKey="campaign-overview-table">
        <FBPage
          innerPageClassName="!bg-[#fefefe]"
          containerClassName="!bg-[#fefefe] !shadow-none"
        >
          <FBPageHeader>
            <FBPageTitleRow>
              <div>
                <FBPageTitle className="!text-fg">Campaigns</FBPageTitle>
                <p className="text-sm text-fg-secondary mt-1">
                  Manage your outreach campaigns from start to finish.
                </p>
              </div>
            </FBPageTitleRow>
            <FbButton
              onClick={() => openModal()}
              className="!bg-brand-bright !text-white hover:!bg-brand-active"
            >
              <PiPlus size={18} />
              New Campaign
            </FbButton>
          </FBPageHeader>

          <div className="pt-3">
            {activeTab === 'campaigns' && (
              <div className="mb-4">
                <div
                  className={cn('mb-3', segmentedPillGroupClass)}
                  role="group"
                  aria-label="Stats time period"
                >
                  {STATS_PERIOD_OPTIONS.map((option) => {
                    const isActive = statsPeriod === option.key
                    return (
                      <button
                        key={option.key}
                        type="button"
                        aria-pressed={isActive}
                        onClick={() => {
                          setStatsPeriod(option.key)
                          setStatsDateRange(undefined)
                        }}
                        className={cn(
                          STATS_PILL_BASE,
                          isActive
                            ? segmentedPillActiveClass
                            : segmentedPillInactiveClass
                        )}
                      >
                        {option.label}
                      </button>
                    )
                  })}
                  <DateRangePicker
                    value={statsDateRange}
                    onChange={(range) => {
                      setStatsDateRange(range)
                      setStatsPeriod('custom')
                    }}
                    renderTrigger={({ open }) => {
                      const isActive = statsPeriod === 'custom'
                      return (
                        <button
                          type="button"
                          aria-pressed={isActive}
                          className={cn(
                            STATS_PILL_BASE,
                            isActive || open
                              ? segmentedPillActiveClass
                              : segmentedPillInactiveClass
                          )}
                        >
                          <PiCalendarBlank size={16} />
                          {customRangeLabel
                            ? `Custom (${customRangeLabel})`
                            : 'Custom'}
                        </button>
                      )
                    }}
                  />
                </div>
                <CampaignsStatsBar stats={stats} />
              </div>
            )}
            <UnderlineTabs
              tabs={[
                {
                  key: 'campaigns',
                  title: 'All Campaigns',
                  count: stats?.total_campaigns ?? null,
                },
                {
                  key: 'replies',
                  title: 'All Replies',
                  count:
                    replySummary?.is_available && replySummary.total != null
                      ? replySummary.total
                      : null,
                },
              ]}
              activeKey={activeTab}
              onChange={setActiveTab}
              idPrefix="campaigns-page"
              aria-label="Campaigns and replies"
              className="mb-3"
            />

            <TabPanel<PageTab>
              idPrefix="campaigns-page"
              tabKey="replies"
              activeKey={activeTab}
            >
              <div
                className="flex flex-col"
                style={{ height: REPLIES_PANEL_HEIGHT }}
              >
                <CampaignRepliesTab />
              </div>
            </TabPanel>

            <TabPanel<PageTab>
              idPrefix="campaigns-page"
              tabKey="campaigns"
              activeKey={activeTab}
            >
              <TableLinkStyleProvider variant="plain">
                <StageColorProvider value="pipeline">
                  <FBPageTableContainer
                    className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
                    style={{ height: CAMPAIGNS_PANEL_HEIGHT }}
                  >
                    <CampaignsTable
                      onEditCampaign={handleRowEdit}
                      tableKey={ALL_CAMPAIGNS_TABLE_KEY}
                    />
                  </FBPageTableContainer>
                </StageColorProvider>
              </TableLinkStyleProvider>
            </TabPanel>
          </div>
        </FBPage>

        <CreateEditCampaignModal
          isOpen={showModal}
          onClose={hideModal}
          editingCampaign={selectedCampaign}
        />
        <CreateCampaignModalV2
          show={showV2Modal}
          onHide={() => setShowV2Modal(false)}
        />
      </DataTableProvider>
    )
  }

  const tabs: { key: PageTab; title: string | React.JSX.Element }[] = [
    { key: 'campaigns', title: 'Campaigns' },
    {
      key: 'replies',
      title: (
        <span>
          Replies
          {replySummary?.is_available && replySummary.total !== undefined && (
            <i className="font-extralight not-italic">
              {' '}
              ({replySummary.total})
            </i>
          )}
        </span>
      ),
    },
  ]

  return (
    <DataTableProvider tableKey="campaign-overview-table">
      <FBPage innerPageClassName="!bg-[#fefefe]">
        <FBPageHeader>
          <FBPageTitleRow>
            <FBPageTitle>
              <span className={'font-normal'}>Campaigns</span>
            </FBPageTitle>
          </FBPageTitleRow>
          {activeTab === 'campaigns' && (
            <FbButton style={{ width: 200 }} onClick={() => openModal()}>
              Create Campaign
            </FbButton>
          )}
        </FBPageHeader>

        {featureFlag.enableEmailSequencing && (
          <FBPageInnerTabsContainer>
            <Tabs
              tabs={tabs}
              selectedTab={activeTab}
              setSelectedTab={setActiveTab}
              tabStyle={{ width: '120px' }}
            />
          </FBPageInnerTabsContainer>
        )}

        {activeTab === 'campaigns' && (
          <TableLinkStyleProvider variant="plain">
            <StageColorProvider value="pipeline">
              <FBPageTableContainer
                className="flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]"
                style={{
                  height: 'calc(var(--base-page-table-height) - 47px)',
                }}
              >
                <CampaignsTable
                  onEditCampaign={handleRowEdit}
                  tableKey={ALL_CAMPAIGNS_TABLE_KEY}
                />
              </FBPageTableContainer>
            </StageColorProvider>
          </TableLinkStyleProvider>
        )}

        {activeTab === 'replies' && (
          <div style={{ height: 'calc(var(--base-page-table-height) - 60px)' }}>
            <CampaignRepliesTab />
          </div>
        )}
      </FBPage>
      <CreateEditCampaignModal
        isOpen={showModal}
        onClose={hideModal}
        editingCampaign={selectedCampaign}
      />
      <CreateCampaignModalV2
        show={showV2Modal}
        onHide={() => setShowV2Modal(false)}
      />
    </DataTableProvider>
  )
}
